import { createFileRoute, Link } from '@tanstack/react-router'
import { SETTINGS_FIELDS, SETTINGS_STACK } from './-components/settings/settings-section'
import { useSuspenseQuery, useMutation, noop } from '@tanstack/react-query'
import { Suspense } from 'react'
import { useLingui } from '@lingui/react/macro'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  AlertTriangleIcon,
  KeyIcon,
  LinkIcon,
  PaletteIcon,
  PlusIcon,
  ShieldCheckIcon,
  TrashIcon,
  XIcon,
} from '@/routes/_app/-components/settings/settings-icons'
import { trpc } from '@/main'
// Same shape as beta.generate: this route IS the app-settings form and `updateAppFormSchema` is its validator,
// so zod is on its critical path by definition.
// oxlint-disable-next-line shared-barrel/no-barrel-values -- this route is the form; see above.
import { updateAppFormSchema } from '@/lib/form-schemas'
import { useAppForm } from '@/lib/use-app-form'
import * as v from 'valibot'
import { toast } from '@/lib/toast'
import { formatNumber } from '@/lib/utils'
import { CopyableCredential } from './-components/api/copyable-credential'
import { RegenerateSecret } from './-components/api/regenerate-secret'
import { TypeToConfirmDialog } from './-components/shared/type-to-confirm-dialog'
import { DarkModeSelector } from './-components/api/dark-mode-selector'
import { SectionCard } from './-components/api/section-card'
import { IntegrationSettingsRoutePending } from './-components/settings/account-pending'
import { SandboxNotice } from './-components/api/sandbox-notice'

export const Route = createFileRoute('/_app/account/integrations/$appId/settings')({
  // What still blocks deleting this client. Warmed, not awaited: only the delete section at the foot reads it.
  context: ({ params }) => ({ deleteBlockersQueryOptions: trpc.apps.deleteBlockers.queryOptions(params.appId) }),
  loader: ({ context }) => {
    context.queryClient.query(context.deleteBlockersQueryOptions).catch(noop)
  },
  component: RouteComponent,
  pendingComponent: IntegrationSettingsRoutePending,
})

function RouteComponent() {
  const { t } = useLingui()
  const { appId } = Route.useParams()
  // The parent `$appId` layout declares and warms this — context flows down, so the tab reads ITS field.
  const { data: app } = useSuspenseQuery(Route.useRouteContext().appQueryOptions)

  return (
    <div className={SETTINGS_STACK}>
      {app.app.isSandbox && <SandboxNotice />}

      <SectionCard
        icon={KeyIcon}
        title={t`API Credentials`}
        description={t`Use these credentials to authenticate your API requests. Keep your Client Secret secure.`}
      >
        <div className={SETTINGS_FIELDS}>
          <CopyableCredential
            label={t`Client ID`}
            value={app.app.clientId}
            description={t`Public identifier for your app`}
          />
          <CopyableCredential
            label={t`Client Secret`}
            value={app.app.clientSecret}
            description={t`Keep this secret - never expose in client-side code`}
            hidden
          />
          <RegenerateSecret appClientId={appId} />

          <div className="bg-muted/20 flex flex-col gap-2 rounded-lg border border-dashed p-3">
            <p className="text-muted-foreground text-xs font-medium">{t`Authentication Example`}</p>
            <pre className="text-muted-foreground/80 overflow-x-auto text-[11px]">
              <code>{`Authorization: Bearer <your_token>`}</code>
            </pre>
          </div>
        </div>
      </SectionCard>

      <AppearanceCard appId={appId} app={app.app} />
      <DomainsCard appId={appId} app={app.app} />
      <CallbacksCard appId={appId} app={app.app} />

      <DeleteClientSection appId={appId} />
    </div>
  )
}

type AppSettings = {
  name: string | null
  domain: string[] | null
  accentColor: string | null
  darkMode: boolean | null
  applicantButtonExit: string | null
  jobButtonExit: string | null
  enableJobAddApplicantButton: boolean | null
}

// Each card is its own form and saves only its own fields: `apps.update` is a PATCH, so the other cards' stored
// values stay as they are, and their own unsaved edits too (an untouched form re-baselines on the refetch).
function useSaveApp(appId: string) {
  const { t } = useLingui()
  const { mutateAsync: updateApp } = useMutation(trpc.apps.update.mutationOptions())
  return async (data: Parameters<typeof updateApp>[0]['data']) => {
    await updateApp({ appClientId: appId, data })
    toast.success(t`Settings saved successfully`)
  }
}

/** A card's fields inside their own <form>, so Enter in a field saves that card and nothing else. */
function CardForm(props: { onSubmit: () => void; children: React.ReactNode }) {
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        e.stopPropagation()
        props.onSubmit()
      }}
      className={SETTINGS_FIELDS}
    >
      {props.children}
    </form>
  )
}

// Text fields start empty rather than null, as they read once cleared; the schema keeps null for the server's shape.
const appearanceDefaults = (
  app: AppSettings,
): { name: string | null; accentColor: string | null; darkMode: boolean | null } => ({
  name: app.name ?? '',
  accentColor: app.accentColor ?? '',
  darkMode: app.darkMode ?? null,
})

const callbackDefaults = (
  app: AppSettings,
): { applicantButtonExit: string | null; jobButtonExit: string | null; enableJobAddApplicantButton: boolean } => ({
  applicantButtonExit: app.applicantButtonExit ?? '',
  jobButtonExit: app.jobButtonExit ?? '',
  enableJobAddApplicantButton: app.enableJobAddApplicantButton ?? false,
})

function AppearanceCard(props: { appId: string; app: AppSettings }) {
  const { t } = useLingui()
  const save = useSaveApp(props.appId)
  const form = useAppForm({
    validators: { onSubmit: v.pick(updateAppFormSchema, ['name', 'accentColor', 'darkMode']) },
    defaultValues: appearanceDefaults(props.app),
    // Empty in the form, null on the server: a cleared name or colour is stored as unset.
    onSubmit: ({ value }) =>
      save({ ...value, name: value.name?.trim() || null, accentColor: value.accentColor || null }),
  })

  return (
    <SectionCard
      icon={PaletteIcon}
      title={t`Appearance & Branding`}
      testId="appearance-card"
      actions={
        <form.AppForm>
          <form.SaveDiscardButtons />
        </form.AppForm>
      }
      description={t`Customize how assessments appear when embedded in your application.`}
    >
      <CardForm onSubmit={() => void form.handleSubmit()}>
        <form.AppField name="name" children={(field) => <field.TextField label={t`App Name`} autoComplete="off" />} />

        <form.AppField
          name="accentColor"
          children={(field) => <field.ColorField label={t`Accent Color`} placeholder="#33CCAA" autoComplete="off" />}
        />

        <div className="w-fit">
          <form.AppField
            name="darkMode"
            children={(field) => (
              <DarkModeSelector value={field.state.value} onChange={(value) => field.handleChange(value)} />
            )}
          />
        </div>
      </CardForm>
    </SectionCard>
  )
}

function DomainsCard(props: { appId: string; app: AppSettings }) {
  const { t } = useLingui()
  const save = useSaveApp(props.appId)
  const form = useAppForm({
    validators: { onSubmit: v.pick(updateAppFormSchema, ['domain']) },
    defaultValues: { domain: props.app.domain ?? [] },
    onSubmit: ({ value }) => save(value),
  })

  const handleAddDomain = () => {
    form.setFieldValue('domain', [...form.getFieldValue('domain'), ''])
  }

  const handleDeleteDomain = (index: number) => {
    const domains = form.getFieldValue('domain')
    form.setFieldValue(
      'domain',
      domains.filter((_, i) => i !== index),
    )
  }

  return (
    <SectionCard
      icon={ShieldCheckIcon}
      title={t`Security & Allowed Domains`}
      testId="domains-card"
      actions={
        <form.AppForm>
          <form.SaveDiscardButtons />
        </form.AppForm>
      }
      description={t`Restrict API access to specific domains. Leave empty to allow requests from any origin.`}
    >
      <CardForm onSubmit={() => void form.handleSubmit()}>
        <div className="flex flex-col gap-3">
          <form.AppField
            name="domain"
            mode="array"
            children={(field) =>
              field.state.value.length === 0 ? (
                <div className="bg-muted/20 flex items-center gap-2 rounded-lg border border-dashed p-4">
                  <div className="flex size-8 items-center justify-center rounded-full bg-amber-500/10 text-amber-600">
                    <AlertTriangleIcon className="size-4" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">{t`No domain restrictions`}</p>
                    <p className="text-muted-foreground text-xs">{t`All domains can make API requests. Add domains for production security.`}</p>
                  </div>
                </div>
              ) : (
                <div className={SETTINGS_FIELDS}>
                  {field.state.value.map((domain, domainIdx) => (
                    <div key={`domain-${domain || domainIdx}`} className="flex items-center gap-2">
                      <Input
                        placeholder={t`https://example.com`}
                        defaultValue={domain}
                        onChange={(e) => {
                          const newDomains = [...field.state.value]
                          newDomains[domainIdx] = e.target.value
                          field.handleChange(newDomains)
                        }}
                        className="text-sm"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={t`Remove domain`}
                        onClick={() => handleDeleteDomain(domainIdx)}
                        className="text-muted-foreground hover:text-destructive shrink-0"
                      >
                        <XIcon className="size-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )
            }
          />
          <Button type="button" variant="secondary" onClick={handleAddDomain} className="w-fit">
            <PlusIcon className="size-4" />
            {t`Add Domain`}
          </Button>
        </div>
      </CardForm>
    </SectionCard>
  )
}

function CallbacksCard(props: { appId: string; app: AppSettings }) {
  const { t } = useLingui()
  const save = useSaveApp(props.appId)
  const form = useAppForm({
    validators: {
      onSubmit: v.pick(updateAppFormSchema, ['applicantButtonExit', 'jobButtonExit', 'enableJobAddApplicantButton']),
    },
    defaultValues: callbackDefaults(props.app),
    onSubmit: ({ value }) =>
      save({
        ...value,
        applicantButtonExit: value.applicantButtonExit || null,
        jobButtonExit: value.jobButtonExit || null,
      }),
  })

  return (
    <SectionCard
      icon={LinkIcon}
      title={t`Integration Callbacks`}
      testId="callbacks-card"
      actions={
        <form.AppForm>
          <form.SaveDiscardButtons />
        </form.AppForm>
      }
      description={t`Configure redirect URLs and button behavior for seamless integration.`}
    >
      <CardForm onSubmit={() => void form.handleSubmit()}>
        <div className="flex flex-col gap-1.5">
          <form.AppField
            name="applicantButtonExit"
            children={(field) => <field.TextField label={t`Applicant Exit URL`} />}
          />
          <p className="text-muted-foreground text-[11px]">{t`Applicants will be redirected here with ?applicantId=<id> parameter`}</p>
        </div>

        <div className="flex flex-col gap-1.5">
          <form.AppField name="jobButtonExit" children={(field) => <field.TextField label={t`Job Exit URL`} />} />
          <p className="text-muted-foreground text-[11px]">{t`Job descriptions will redirect here with ?jobId=<id> parameter`}</p>
        </div>

        <div className="border-t pt-2">
          <form.AppField
            name="enableJobAddApplicantButton"
            children={(field) => (
              <field.SwitchField label={t`Allow adding applicants to jobs from the embedded interface`} />
            )}
          />
        </div>
      </CardForm>
    </SectionCard>
  )
}

/**
 * Deleting a client that still has candidates, companies or jobs would drop only the LINKS and leave those rows
 * orphaned, so the server refuses it (`APP_HAS_LINKED_ENTITIES`) and this section says what is left, with the way to
 * it: the client's own tabs, or the playground's Reset for a sandbox. The button stays off until the count is zero;
 * a stale page still gets the server's refusal as a toast, and the count is fetched again.
 */
function DeleteClientSection(props: { appId: string }) {
  const { t } = useLingui()
  return (
    <SectionCard
      icon={TrashIcon}
      title={t`Delete API Client`}
      description={t`Permanently remove this client and revoke all access. This cannot be undone.`}
      variant="danger"
      testId="delete-client-section"
    >
      {/* Only this body waits on the count; until it lands the button is simply off. */}
      <Suspense
        fallback={
          <DeleteClientRow>
            <Button type="button" variant="destructive" disabled>
              <TrashIcon className="size-4" />
              {t`Delete`}
            </Button>
          </DeleteClientRow>
        }
      >
        <DeleteClientControls appId={props.appId} />
      </Suspense>
    </SectionCard>
  )
}

function DeleteClientControls(props: { appId: string }) {
  const { t } = useLingui()
  const { data: blockers, refetch: refetchBlockers } = useSuspenseQuery(
    Route.useRouteContext().deleteBlockersQueryOptions,
  )
  const { mutateAsync: deleteApp, isPending: isDeleting } = useMutation(trpc.apps.delete.mutationOptions())
  const blocked = blockers.candidates + blockers.companies + blockers.jobs > 0

  const deleteButton = (
    <Button type="button" variant="destructive" disabled={blocked || isDeleting}>
      <TrashIcon className="size-4" />
      {t`Delete`}
    </Button>
  )

  return (
    <div className={SETTINGS_FIELDS}>
      {blocked && <DeleteBlockedNotice appId={props.appId} blockers={blockers} />}
      <DeleteClientRow>
        {/* The typed confirmation only exists once nothing blocks: a blocked client has no dialog to open. */}
        {blocked ? (
          deleteButton
        ) : (
          <TypeToConfirmDialog
            trigger={deleteButton}
            title={t`Delete client`}
            consequence={t`Every integration using this client's credentials stops working at once. This cannot be undone.`}
            instruction={t`Type "Delete client" (without quotes) to confirm`}
            phrase={t`Delete client`}
            actionLabel={t`Delete client`}
            pending={isDeleting}
            onConfirm={async () => {
              try {
                await deleteApp(props.appId)
              } catch (error) {
                // The global mutation handler toasted the reason; a refusal only revalidates on success, so re-count.
                void refetchBlockers()
                throw error
              }
              // No invalidateQueries here: the hard navigation below tears down and rebuilds the whole cache,
              // so invalidating first is pure wasted work.
              window.location.href = '/account/integrations/guide'
            }}
          />
        )}
      </DeleteClientRow>
    </div>
  )
}

function DeleteClientRow(props: { children: React.ReactNode }) {
  const { t } = useLingui()
  return (
    <div className="flex items-center justify-between gap-4">
      <div className="flex flex-col gap-0.5">
        <p className="text-sm font-medium">{t`Remove this API client`}</p>
        <p className="text-muted-foreground text-xs">{t`All integrations using these credentials will stop working.`}</p>
      </div>
      {props.children}
    </div>
  )
}

function DeleteBlockedNotice(props: {
  appId: string
  blockers: { candidates: number; companies: number; jobs: number; isSandbox: boolean }
}) {
  const { t, i18n } = useLingui()
  const { isSandbox } = props.blockers
  const count = props.blockers
  // Which parts and which plural come from the numbers; the messages show them formatted.
  const candidates = formatNumber(i18n.locale, count.candidates, 0)
  const companies = formatNumber(i18n.locale, count.companies, 0)
  const jobs = formatNumber(i18n.locale, count.jobs, 0)
  const parts = [
    count.candidates > 0 && (count.candidates === 1 ? t`${candidates} candidate` : t`${candidates} candidates`),
    count.companies > 0 && (count.companies === 1 ? t`${companies} company` : t`${companies} companies`),
    count.jobs > 0 && (count.jobs === 1 ? t`${jobs} job` : t`${jobs} jobs`),
  ].filter((part): part is string => !!part)
  const list = new Intl.ListFormat(undefined, { style: 'long', type: 'conjunction' }).format(parts)
  const linkClass = 'text-foreground font-medium underline underline-offset-2'

  return (
    <div data-testid="delete-client-blocked" className="bg-destructive/10 flex gap-2 rounded-lg p-3 text-sm">
      <AlertTriangleIcon className="text-destructive mt-0.5 size-4 shrink-0" />
      <div className="flex flex-col gap-1.5">
        {isSandbox ? (
          <>
            <p>{t`This sandbox still has ${list} from the tutorial.`}</p>
            <p className="text-muted-foreground">
              {t`Reset the playground to delete them first.`}{' '}
              <Link to="/account/integrations/guide" className={linkClass}>
                {t`Open the guide`}
              </Link>
            </p>
          </>
        ) : (
          <>
            <p>{t`This client still has ${list} linked. Unlink or delete them first.`}</p>
            <p className="text-muted-foreground flex flex-wrap gap-x-3">
              {count.candidates > 0 && (
                <Link
                  to="/account/integrations/$appId/applicants"
                  params={{ appId: props.appId }}
                  className={linkClass}
                >
                  {t`Candidates`}
                </Link>
              )}
              {count.companies > 0 && (
                <Link to="/account/integrations/$appId/companies" params={{ appId: props.appId }} className={linkClass}>
                  {t`Companies`}
                </Link>
              )}
              {count.jobs > 0 && (
                <Link to="/account/integrations/$appId/jobs" params={{ appId: props.appId }} className={linkClass}>
                  {t`Jobs`}
                </Link>
              )}
            </p>
          </>
        )}
      </div>
    </div>
  )
}
